import type { ComponentProps, ReactNode } from 'react';
import {
  LuCircleDot,
  LuCircleOff,
  LuColumns3,
  LuDroplet,
  LuGrip,
  LuInfo,
  LuLayers3,
  LuMaximize2,
  LuMoveDown,
  LuMoveVertical,
  LuZap,
} from 'react-icons/lu';

import type { PatternDesignOptimisation } from '~api/services';
import { PropertyGroup, PropertyItem, TooltipContent, TooltipRoot, TooltipTrigger } from '~components';
import { Button } from '~components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '~components/ui/card';

/* -------------------------------------------------------------------------- */
/*                                    Types                                   */
/* -------------------------------------------------------------------------- */

type OptimiserInfoProps = {
  /** The selected optimization data to display */
  selectedOptimisation: PatternDesignOptimisation;
  /** Optional additional CSS classes for the component */
  className?: string;
};

type Parameter = Pick<ComponentProps<typeof PropertyItem>, 'label' | 'icon' | 'unit'> & {
  /** Function to get the formatted value of the parameter from the optimization data */
  getValue: (opt: PatternDesignOptimisation) => string | number | ReactNode;
};

/* -------------------------------------------------------------------------- */
/*                                  Constants                                 */
/* -------------------------------------------------------------------------- */

/**
 * Design parameters for blast optimization
 */
const DESIGN_PARAMETERS: Parameter[] = [
  {
    label: 'Efficiency',
    icon: <LuZap size={14} color="var(--color-primary)" />,
    getValue: (opt) => (opt.efficiency != null ? `${(opt.efficiency * 100).toFixed(1)}` : 'N/A'),
    unit: '%',
  },
  {
    label: 'Size Delta',
    icon: <LuCircleDot size={14} color="var(--color-primary)" />,
    getValue: (opt) => (opt.size_delta != null ? `${opt.size_delta.toFixed(1)}` : 'N/A'),
    unit: 'cm',
  },
  {
    label: 'Bench Height',
    icon: <LuMoveVertical size={14} color="var(--color-primary)" />,
    getValue: (opt) => (opt.bench_height != null ? `${opt.bench_height}` : 'N/A'),
    unit: 'm',
  },
  {
    label: 'Burden',
    icon: <LuMaximize2 size={14} color="var(--color-primary)" />,
    getValue: (opt) => (opt.burden != null ? `${opt.burden}` : 'N/A'),
    unit: 'm',
  },
  {
    label: 'Spacing',
    icon: <LuColumns3 size={14} color="var(--color-primary)" />,
    getValue: (opt) => (opt.spacing != null ? `${opt.spacing}` : 'N/A'),
    unit: 'm',
  },
  {
    label: 'Diameter',
    icon: <LuCircleOff size={14} color="var(--color-primary)" />,
    getValue: (opt) => (opt.diameter != null ? `${(opt.diameter * 1000).toFixed(0)}` : 'N/A'),
    unit: 'mm',
  },
  {
    label: 'Holes',
    icon: <LuGrip size={14} color="var(--color-primary)" />,
    getValue: (opt) => (opt.holes != null ? `${opt.holes}` : 'N/A'),
  },
];

/**
 * Statistical parameters for blast optimization
 */
const STATISTICS: Parameter[] = [
  {
    label: 'Explosive Density',
    icon: <LuDroplet className="size-3.5" />,
    getValue: (opt) => (
      <div className="flex items-center gap-1">
        {String(opt.statistics?.density_mode ?? 'N/A')}
        <TooltipRoot>
          <TooltipTrigger asChild>
            <Button icon variant="ghost" size="sm" className="size-4" aria-label="More info about Explosive Density">
              <LuInfo className="size-4" color="var(--color-info)" aria-hidden="true" />
            </Button>
          </TooltipTrigger>
          <TooltipContent className="text-pretty" side="right">
            The most common density in the blast pattern design
          </TooltipContent>
        </TooltipRoot>
      </div>
    ),
  },
  {
    label: 'Stem Mode',
    icon: <LuLayers3 className="size-3.5" />,
    getValue: (opt) => (opt.statistics?.stem_mode != null ? `${opt.statistics.stem_mode.toFixed(2)}` : 'N/A'),
    unit: 'm',
  },
  {
    label: 'Total Depth',
    icon: <LuMoveDown className="size-3.5" />,
    getValue: (opt) => (opt.statistics?.total_depth != null ? `${opt.statistics.total_depth.toFixed(2)}` : 'N/A'),
    unit: 'm',
  },
];

/* -------------------------------------------------------------------------- */
/*                               Main component                               */
/* -------------------------------------------------------------------------- */

/**
 * Component to display detailed information about a selected blast optimization,
 * including key design parameters and statistics.
 *
 * @example
 * ```tsx
 *   <OptimiserInfo selectedOptimisation={selectedOpt} className="mt-4" />
 * ```
 */
const OptimiserInfo = ({ selectedOptimisation, className }: OptimiserInfoProps) => {
  return (
    <Card className={className}>
      <CardHeader className="border-b">
        <CardTitle className="font-medium text-foreground text-sm uppercase">Parameters</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <PropertyGroup withBorder>
          {DESIGN_PARAMETERS.map(({ label, icon, getValue, unit }) => (
            <PropertyItem
              key={label}
              icon={icon}
              label={label}
              value={getValue(selectedOptimisation)}
              unit={unit}
              contained
            />
          ))}
        </PropertyGroup>
        <PropertyGroup>
          {STATISTICS.map(({ label, icon, getValue, unit }) => (
            <PropertyItem key={label} icon={icon} label={label} value={getValue(selectedOptimisation)} unit={unit} />
          ))}
        </PropertyGroup>
      </CardContent>
    </Card>
  );
};

/* -------------------------------------------------------------------------- */
/*                                   Exports                                  */
/* -------------------------------------------------------------------------- */

export { OptimiserInfo };
